<template>
  <v-card>
    <v-card-title class="d-flex">
      <v-icon start color="grey">mdi-filter</v-icon>
      Filter
      <v-icon @click="save" class="ml-auto" end color="grey">mdi-close</v-icon>
    </v-card-title>
    <v-card-text class="px-3 py-8">
      <v-form :ref="form">
        <sorter v-model="sorts" :sortableFields="sortableFields" :SortTypes="SortTypes"/>
        <v-text-field
          v-model="fulltextSearch"
          variant="outlined"
          hide-details
          placeholder="Freitextsuche"
          clearable
          @click:clear="fulltextSearch = ''"
        />
        <v-select
          variant="plain"
          messages=""
          density="compact"
          :items="allSearchInItems"
          multiple
          item-title="text"
          item-value="value"
          v-model="filters.Fulltext$Fulltext.fields"
        >
          <template #selection="{ item }">
            <v-chip variant="outlined" class="black--color" color="grey" size="x-small">
              <span>{{ item.title }}</span>
            </v-chip>
          </template>
        </v-select>
        <v-select v-model="erledigt" label="Erledigt" clearable
                  item-title="text"
                  item-value="value"
                  :items="[
            {
              text: 'Ja',
              value: true,
            },
            {
              text: 'Nein',
              value: false,
            },
          ]"/>
        <v-select v-model="gelesen" label="Gelesen" clearable
                  item-title="text"
                  item-value="value"
                  :items="[
            {
              text: 'Alle',
              value: null,
            },
            {
              text: 'Ja',
              value: true,
            },
            {
              text: 'Nein',
              value: false,
            },
          ]"/>
        <date-picker v-model="DatumFrom" label="Anlage-Datum von"/>
        <date-picker v-model="DatumTo" label="Anlage-Datum bis"/>
        <date-picker v-model="WDatumFrom" label="Wiedervorlage-Datum von"/>
        <date-picker v-model="WDatumTo" label="Wiedervorlage-Datum bis"/>
        <SelectBediener show-label v-model='bediener'/>
        <v-select
          v-model="filters['KundStatus$In']"
          :items="[
            { value: '1', text: 'Interessenten' },
            { value: '2', text: 'Kunden' },
            { value: '3', text: 'Ehemalige Kunden' },
            { value: '9', text: 'Lieferanten' },
          ]"
          multiple
          item-title="text"
          item-value="value"
          label="Kundentyp"
          clearable
        />
      </v-form>
    </v-card-text>
    <v-divider></v-divider>

    <v-card-actions class="text-center">
      <v-btn color="grey" class="flex-1" variant="outlined" size="large" @click="reset">Zurücksetzen</v-btn>
      <v-btn class="flex-1 btn-navigator-primary" size="large" @click="save">Speichern</v-btn>
    </v-card-actions>
  </v-card>
</template>

<script setup lang="ts">
import {computed, ref, watch} from 'vue';
import {FulltextSearchField, ValueType} from '@/api/meta/filters';
import {useContactKindStore} from '@/store/contactKind';
import DatePicker from '../DatePicker.vue';
import Sorter from '../Sorter.vue';
import {useSettings} from '@/store/settings';
import SelectBediener from '@/components/SelectBediener.vue';
import {useFilterSort} from '@/composables/filterSort';
import useFollowupsStore from '@/store/followups';
import {VNodeRef} from 'vue';

const contactKindStore = useContactKindStore();
contactKindStore.fetch().catch(console.error);
const emits = defineEmits(['close']);
const store = useFollowupsStore();
const {save, reset, filters, sorts, SortTypes} = useFilterSort(store, emits);

const allSearchInItems = ref<Array<{ text: string, value: FulltextSearchField }>>([
  {
    text: 'Bediener',
    value: {
      path: ['Bediener', 'Bediener'],
      valueType: ValueType.StringContains,
    },
  },
  {
    text: 'Kundennummer',
    value: {
      path: ['KundenNr'],
      valueType: ValueType.NumberStartsWith,
    },
  },
  {
    text: 'Kundenname',
    value: {
      path: ['KundeName'],
      valueType: ValueType.StringContains,
    },
  },
  {
    text: 'Titel',
    value: {
      path: ['Thema'],
      valueType: ValueType.StringContains,
    },
  },
]);

const sortableFields = ref([{
  text: 'Datum',
  value: 'AnlageDatum',
},
{
  text: 'Kundenname',
  value: 'KundeName',
}]);
const form = ref<HTMLFormElement & VNodeRef | undefined>();

const WDatumFrom = computed({
  get() {
    return filters.value.Bediener$Any.WiedervorlageDatum$From;
  },
  set(val: string | undefined) {
    if (!val) filters.value.Bediener$Any.WiedervorlageDatum$From = undefined;
    else filters.value.Bediener$Any.WiedervorlageDatum$From = val;
  },
});
const WDatumTo = computed({
  get() {
    return filters.value.Bediener$Any.WiedervorlageDatum$To;
  },
  set(val: string | undefined) {
    if (!val) filters.value.Bediener$Any.WiedervorlageDatum$To = undefined;
    else filters.value.Bediener$Any.WiedervorlageDatum$To = val;
  },
});
const DatumFrom = computed({
  get() {
    return filters.value.AnlageDatum$From;
  },
  set(val: string | undefined) {
    if (!val) filters.value.AnlageDatum$From = undefined;
    else filters.value.AnlageDatum$From = val;
  },
});
const DatumTo = computed({
  get() {
    return filters.value.AnlageDatum$To;
  },
  set(val: string | undefined) {
    if (!val) filters.value.AnlageDatum$To = undefined;
    else filters.value.AnlageDatum$To = val;
  },
});
const fulltextSearch = computed({
  set(sWord: string) {
    const words = (sWord ?? '').split(' ').filter((w) => !!w);
    filters.value.Fulltext$Fulltext.words = words;
  },
  get(): string {
    const words = filters.value.Fulltext$Fulltext.words;

    return words?.join(' ') || '';
  },
});
const bediener = computed({
  set(bediener: string) {
    filters.value.Bediener$Any.Bediener = bediener;
  },
  get(): string {
    return filters.value.Bediener$Any.Bediener ?? useSettings().userShort ?? '';
  },
});
const erledigt = computed({
  set(val: boolean | null | undefined) {
    if (!val && val !== false) delete filters.value.Bediener$Any['Erledigt/Status'];
    else filters.value.Bediener$Any['Erledigt/Status'] = val;
  },
  get(): boolean | undefined {
    return filters.value.Bediener$Any['Erledigt/Status'] || false;
  },
});
const gelesen = computed({
  set(val: boolean | null | undefined) {
    if (val == null) delete filters.value.Bediener$Any['Gelesen/Status'];
    else filters.value.Bediener$Any['Gelesen/Status'] = val;
  },
  get(): boolean | null | undefined {
    if (filters.value.Bediener$Any['Gelesen/Status'] == undefined) return null;
    return filters.value.Bediener$Any['Gelesen/Status'];
  },
});

function ruleInt(val: string | number | undefined | null): string | true {
  if (!val) return true;

  if (parseInt(val as string).toString() !== val.toString()) {
    return 'bitte eine ganze Zahl eingeben';
  }

  return true;
}

function ruleUserShort(val: string | undefined | null): string | true {
  if (!val) return true;

  if (val.length > 5) return 'zu lang';

  return true;
}

watch(form, (form) => {
  form?.validate();
});

defineExpose({
  ruleInt,
  ruleUserShort,
  save, reset, filters, sorts, SortTypes,
  form,
  sortableFields,
  allSearchInItems,
  WDatumFrom,
  WDatumTo,
  DatumFrom,
  DatumTo,
  fulltextSearch,
  bediener,
  erledigt,
});
</script>
<style scoped>
.fixed-bottom {
  position: fixed;
  bottom: 0;
  width: 100%;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
}

.v-card__actions {
  justify-content: center;
}
</style>
